/**
 * Shared form validation utilities.
 * Returns an error string or null if valid.
 */

/**
 * Name validation — used for lead names, property titles, contact names.
 * Rules:
 * - Required, trimmed, no empty/space-only strings
 * - Min 2 chars, max configurable (default 100)
 * - Allowed: Buchstaben aller Sprachen (Unicode), spaces, hyphens, apostrophes, periods
 * - Blocked: numbers (unless allowNumbers), special chars, emojis, scripts, SQL injection
 * - No consecutive special chars (-- or '' or -')
 * - Trimmed front and back
 */
export function validateName(value, fieldName = 'Name', { min = 2, max = 100, allowNumbers = false } = {}) {
  if (!value || typeof value !== 'string') return `${fieldName} is required`;

  const trimmed = value.trim();

  if (!trimmed) return `${fieldName} is required`;

  if (trimmed === '""' || trimmed === "''" || trimmed === '``') return `${fieldName} cannot be empty quotes`;

  if (trimmed.length < min) return `${fieldName} must be at least ${min} characters`;
  if (trimmed.length > max) return `${fieldName} must be under ${max} characters`;

  // Check for dangerous patterns (SQL injection, HTML/JS)
  const dangerousPatterns = /(<script|<\/script|javascript:|on\w+=|SELECT\s|INSERT\s|DELETE\s|DROP\s|UPDATE\s|UNION\s|--\s|;\s*DROP)/i;
  if (dangerousPatterns.test(trimmed)) return `${fieldName} contains invalid content`;

  // Erlaubt: Buchstaben aller Sprachen (Umlaute, ß, é, ç, ğ, İ …), Leerzeichen, Bindestrich,
  // Apostroph, Punkt (Dr., Jr.) — optional Ziffern. \p{L}/\p{M} statt A-Z, sonst scheitert jeder Umlaut.
  const allowedPattern = allowNumbers
    ? /^[\p{L}\p{M}0-9\s'\-.]+$/u
    : /^[\p{L}\p{M}\s'\-.]+$/u;
  if (!allowedPattern.test(trimmed)) return `${fieldName}: nur Buchstaben, Leerzeichen, Bindestrich, Apostroph und Punkt erlaubt`;

  // No consecutive special characters (e.g., --, '', -', etc.)
  if (/[\-']{2,}/.test(trimmed)) return `${fieldName} cannot have consecutive special characters`;

  // No leading/trailing hyphens or apostrophes
  if (/^[\-']|[\-']$/.test(trimmed)) return `${fieldName} cannot start or end with a hyphen or apostrophe`;

  return null;
}

export function validateRequired(value, fieldName) {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
}

export function validateEmail(value) {
  if (!value) return null; // optional
  const re = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
  if (!re.test(value.trim())) return 'Invalid email format';
  return null;
}

export function validatePhone(value) {
  if (!value) return null; // optional
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Must start with +
  if (!trimmed.startsWith('+')) return 'Phone must start with + (e.g., +1234567890)';

  // Remove spaces, dashes, parens for digit validation
  const cleaned = trimmed.replace(/[\s\-\(\)]/g, '');

  // Only + followed by digits allowed (no letters, special chars, emojis)
  if (!/^\+[0-9]+$/.test(cleaned)) return 'Phone can only contain digits after the + prefix';

  // Country code + number: minimum +X NNNNNNN (1 digit code + 7 digits = 8 after +)
  const digits = cleaned.slice(1); // remove the +
  if (digits.length < 8) return 'Phone must have country code + at least 7 digits';
  if (digits.length > 15) return 'Phone number too long (max 15 digits)';

  // Not all zeros
  if (/^0+$/.test(digits)) return 'Phone number cannot be all zeros';

  return null;
}

export function validatePositiveNumber(value, fieldName) {
  if (value === '' || value == null) return null; // optional
  const n = Number(value);
  if (isNaN(n) || n < 0) return `${fieldName} must be a positive number`;
  return null;
}

export function validateBudgetRange(min, max) {
  if (min && max && Number(max) < Number(min)) return 'Max budget must be greater than min';
  return null;
}

export function validateMaxLength(value, max, fieldName) {
  if (value && value.length > max) return `${fieldName} must be under ${max} characters`;
  return null;
}

/**
 * Run all validators on a form. Returns { errors: {field: message}, isValid: bool }
 */
export function validateForm(rules) {
  const errors = {};
  for (const [field, validators] of Object.entries(rules)) {
    for (const fn of validators) {
      const err = fn();
      if (err) { errors[field] = err; break; }
    }
  }
  return { errors, isValid: Object.keys(errors).length === 0 };
}
